import { useCallback, useEffect, useState } from 'react';
import { api } from '@/api/client';
import { extractErrorMessage } from '@/api/errors';
import { useAuth } from '@/auth/useAuth';

/**
 * Commission and markup rates.
 *
 * Changing a rate here changes what every practitioner and store is paid on
 * every future transaction, so the editor shows a worked example before
 * confirming and each change is audited to a named admin.
 */

interface PlatformFee {
  key: string;
  rate: number;
  percent: string;
  description: string | null;
  updated_at: string;
  updated_by_admin_id: number | null;
}

interface FeeAudit {
  id: number;
  key: string;
  old_rate: string | null;
  new_rate: string;
  changed_by_admin_id: number | null;
  reason: string | null;
  created_at: string;
}

/** Rates a customer or seller can read in the published terms. */
const CUSTOMER_FACING = new Set(['consultations', 'product_sales']);

/** Keys we do not price with — they only describe what Pairgate pays us. */
const UPSTREAM_OBSERVED = new Set([
  'vtu_airtime_commission',
  'vtu_data_commission',
]);

function formatDate(iso: string | null | undefined) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function formatNaira(n: number) {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    maximumFractionDigits: 2,
  }).format(n);
}

/** A concrete example beats a percentage when someone is about to change one. */
function workedExample(key: string, rate: number): string {
  if (key === 'consultations') {
    return `A ${formatNaira(10000)} consultation pays the practitioner ${formatNaira(10000 * (1 - rate))}.`;
  }
  if (key === 'product_sales') {
    return `A ${formatNaira(10000)} sale pays the store ${formatNaira(10000 * (1 - rate))}.`;
  }
  if (key === 'vtu_data') {
    return `A ${formatNaira(425)} data plan sells for ${formatNaira(Math.ceil(425 * (1 + rate)))}.`;
  }
  if (key === 'vtu_airtime') {
    return rate === 0
      ? 'Airtime sells at face value — the consultant pays exactly what they receive.'
      : `${formatNaira(500)} of airtime would cost the consultant ${formatNaira(Math.ceil(500 * (1 + rate)))}. Face value is public, so this must be shown as a separate fee.`;
  }
  if (UPSTREAM_OBSERVED.has(key)) {
    return `Measured, not chosen: what Pairgate credits us. A ${formatNaira(500)} sale accrues ${formatNaira(500 * rate)}.`;
  }
  return '';
}

export function FeesPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('fees.manage');

  const [fees, setFees] = useState<PlatformFee[]>([]);
  const [audit, setAudit] = useState<FeeAudit[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [draftPercent, setDraftPercent] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [feesRes, auditRes] = await Promise.all([
        api.get<PlatformFee[]>('/back-office/fees'),
        api.get<FeeAudit[]>('/back-office/fees/audit?limit=25'),
      ]);
      setFees(Array.isArray(feesRes.data) ? feesRes.data : []);
      setAudit(Array.isArray(auditRes.data) ? auditRes.data : []);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load platform fees'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const startEdit = (fee: PlatformFee) => {
    setEditing(fee.key);
    // Edited as a percentage — asking an admin to type 0.15 invites 15.
    setDraftPercent((fee.rate * 100).toString());
    setReason('');
  };

  const save = async (fee: PlatformFee) => {
    const percent = Number(draftPercent);
    if (!Number.isFinite(percent) || percent < 0 || percent > 50) {
      setError('Enter a percentage between 0 and 50.');
      return;
    }

    const rate = percent / 100;
    const confirmation = [
      `Change ${fee.key} from ${fee.percent} to ${percent}%?`,
      '',
      workedExample(fee.key, rate),
      CUSTOMER_FACING.has(fee.key)
        ? '\nThis rate appears in the published terms. Check the Terms page still reads correctly after saving.'
        : '',
    ]
      .filter(Boolean)
      .join('\n');

    if (!window.confirm(confirmation)) return;

    setSaving(true);
    setError(null);
    try {
      await api.patch(`/back-office/fees/${fee.key}`, { rate, reason });
      setEditing(null);
      await load();
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to update the fee'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Platform fees</h1>
        <p className="text-sm text-slate-500">
          Commission and markup rates. Changes take effect within a minute
          across all services — no deploy needed.
        </p>
      </div>

      {error && (
        <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <section className="rounded-lg border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2">Rate</th>
                <th className="px-4 py-2">Current</th>
                <th className="px-4 py-2">What it means</th>
                <th className="px-4 py-2">Updated</th>
                {canManage && <th className="px-4 py-2" />}
              </tr>
            </thead>
            <tbody className="text-slate-700">
              {fees.map((fee) => (
                <tr
                  key={fee.key}
                  className="border-b border-slate-100 align-top last:border-b-0"
                >
                  <td className="px-4 py-3">
                    <div className="font-mono text-xs">{fee.key}</div>
                    {CUSTOMER_FACING.has(fee.key) && (
                      <span className="mt-1 inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-700">
                        in published terms
                      </span>
                    )}
                    {UPSTREAM_OBSERVED.has(fee.key) && (
                      <span className="mt-1 inline-block rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-slate-600">
                        measured
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {editing === fee.key ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            max="50"
                            value={draftPercent}
                            onChange={(e) => setDraftPercent(e.target.value)}
                            className="w-20 rounded border border-slate-300 px-2 py-1 text-sm"
                          />
                          <span className="text-slate-500">%</span>
                        </div>
                        <input
                          type="text"
                          placeholder="Reason (optional)"
                          value={reason}
                          onChange={(e) => setReason(e.target.value)}
                          className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
                        />
                      </div>
                    ) : (
                      <span className="font-semibold">{fee.percent}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    {fee.description}
                    <div className="mt-1 text-slate-500">
                      {workedExample(fee.key, fee.rate)}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {formatDate(fee.updated_at)}
                    {fee.updated_by_admin_id && (
                      <div>by admin #{fee.updated_by_admin_id}</div>
                    )}
                  </td>
                  {canManage && (
                    <td className="px-4 py-3 text-right">
                      {editing === fee.key ? (
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => save(fee)}
                            disabled={saving}
                            className="rounded bg-slate-900 px-2.5 py-1 text-xs font-medium text-white disabled:opacity-50"
                          >
                            {saving ? 'Saving…' : 'Save'}
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditing(null)}
                            className="rounded bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => startEdit(fee)}
                          className="rounded bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700"
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
              {!fees.length && !loading && (
                <tr>
                  <td className="px-4 py-6 text-slate-400" colSpan={5}>
                    No fees configured. Run the payments migration.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white">
        <div className="border-b border-slate-200 px-4 py-3">
          <h2 className="text-sm font-semibold text-slate-900">
            Change history
          </h2>
          <p className="text-xs text-slate-500">
            Every rate change, and who made it.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2">When</th>
                <th className="px-4 py-2">Rate</th>
                <th className="px-4 py-2">Change</th>
                <th className="px-4 py-2">Admin</th>
                <th className="px-4 py-2">Reason</th>
              </tr>
            </thead>
            <tbody className="text-slate-700">
              {audit.map((row) => (
                <tr
                  key={row.id}
                  className="border-b border-slate-100 last:border-b-0"
                >
                  <td className="px-4 py-2 text-xs">
                    {formatDate(row.created_at)}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">{row.key}</td>
                  <td className="px-4 py-2 text-xs">
                    {row.old_rate
                      ? `${(Number(row.old_rate) * 100).toFixed(2)}%`
                      : '—'}{' '}
                    → {(Number(row.new_rate) * 100).toFixed(2)}%
                  </td>
                  <td className="px-4 py-2 text-xs">
                    {row.changed_by_admin_id
                      ? `#${row.changed_by_admin_id}`
                      : 'system'}
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-500">
                    {row.reason ?? '—'}
                  </td>
                </tr>
              ))}
              {!audit.length && (
                <tr>
                  <td className="px-4 py-6 text-slate-400" colSpan={5}>
                    No changes recorded.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
